import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [amount, setAmount] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= MAX_POLLS) { setState("pending"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      setAmount(data.amount);
      if (data.payment_status === "paid") { setState("paid"); return; }
      if (data.status === "failed" || data.status === "expired") { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 max-w-lg w-full text-center bg-white border border-[#0A1128]/10 shadow-lg p-10 md:p-14">
        {state === "checking" && (<><Loader2 className="mx-auto animate-spin text-[#D4AF37]" size={48} /><h1 className="mt-6 font-display text-3xl text-[#0A1128]">{t("pay.confirming")}</h1><p className="mt-3 text-[#4B5563]">{t("pay.confirmingSub")}</p></>)}
        {state === "paid" && (<><CheckCircle2 className="mx-auto text-[#D4AF37]" size={56} /><h1 className="mt-6 font-display text-4xl text-[#0A1128]">{t("pay.thanks")}</h1><p className="mt-4 text-[#4B5563] leading-relaxed">{amount ? `$${amount} ` : ""}{t("pay.thanksBody")}</p></>)}
        {state === "pending" && (<><Loader2 className="mx-auto text-[#D4AF37]" size={48} /><h1 className="mt-6 font-display text-3xl text-[#0A1128]">{t("pay.processing")}</h1><p className="mt-3 text-[#4B5563]">{t("pay.processingSub")}</p></>)}
        {state === "error" && (<><XCircle className="mx-auto text-[#1D3557]" size={48} /><h1 className="mt-6 font-display text-3xl text-[#0A1128]">{t("pay.error")}</h1><p className="mt-3 text-[#4B5563]">{t("pay.errorSub")}</p></>)}
        <Link to="/" data-testid="back-home-link" className="inline-block mt-8 px-8 py-3 bg-[#0A1128] text-white font-semibold hover:bg-[#1D3557] transition-colors">{t("pay.home")}</Link>
      </motion.div>
    </div>
  );
}
